import React, { useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Divider,
  HStack,
  Switch,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { signOutUser } from '../../utils/signout';

const SettingsTile = ({ title, children, onClick }) => {
  return (
    <Box 
      p={3} 
      borderRadius="md" 
      boxShadow="sm"
      width="100%"
      onClick={onClick}
      cursor={onClick ? "pointer" : "default"}
      _hover={onClick ? { bg: 'gray.100' } : {}}
    >
      <HStack justify="space-between" align="center">
        <Text fontWeight="medium">{title}</Text>
        {children}
      </HStack>
    </Box>
  );
};

const Settings = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();

  const [notifications, setNotifications] = useState(true);
  const [twoFactor, setTwoFactor] = useState(false);
  const [privateAccount, setPrivateAccount] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);

  const handleChangePassword = () => {
    toast({
      title: "Change Password",
      description: "Redirecting to change password page...",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleContactSupport = () => {
    toast({
      title: "Contact Support",
      description: "Redirecting to support page...",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleLogout = () => {
    signOutUser();
  };

  return (
    <Flex 
      width="100%" 
      height="100vh" 
      alignItems="flex-start"
      justifyContent="flex-start"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        'scrollbarWidth': 'none',
        '-ms-overflow-style': 'none',
      }}
    >
      <Box 
        width="100%" 
        maxW="100%" 
        mx="auto" 
        p={{ base: 4, md: 8 }}
      >
        <VStack spacing={{ base: 4, md: 6 }} align="flex-start" width="100%">
          <Heading size="lg" mb={{ base: 2, md: 4 }}>Settings</Heading>

          <VStack spacing={3} align="flex-start" width="100%">
            <Heading size="md" mb={2}>Account</Heading>
            <SettingsTile title="Private Account">
              <Switch colorScheme="blue" isChecked={privateAccount} onChange={() => setPrivateAccount(!privateAccount)} />
            </SettingsTile>
            <SettingsTile title="Two-Factor Authentication">
              <Switch colorScheme="blue" isChecked={twoFactor} onChange={() => setTwoFactor(!twoFactor)} />
            </SettingsTile>
            <SettingsTile title="Change Password" onClick={handleChangePassword}>
              <ChevronRightIcon />
            </SettingsTile>
            <SettingsTile title="Contact Support" onClick={handleContactSupport}>
              <ChevronRightIcon />
            </SettingsTile>
          </VStack>

          <Divider width="100%" />

          <VStack spacing={3} align="flex-start" width="100%">
            <Heading size="md" mb={2}>Notifications</Heading>
            <SettingsTile title="Push Notifications">
              <Switch colorScheme="blue" isChecked={notifications} onChange={() => setNotifications(!notifications)} />
            </SettingsTile>
            <SettingsTile title="Email Notifications">
              <Switch colorScheme="blue" isChecked={emailNotifications} onChange={() => setEmailNotifications(!emailNotifications)} />
            </SettingsTile>
          </VStack>

          <Divider width="100%" />

          <VStack spacing={3} align="flex-start" width="100%">
            <Button 
              colorScheme="blue" 
              variant="solid" 
              borderRadius="full"
              width="100%"
              onClick={handleLogout}
            >
              Logout
            </Button>
            <Button 
              colorScheme="red" 
              variant="outline" 
              borderRadius="full"
              width="100%"
              onClick={onOpen}
              _hover={{ bg: 'red.500', color: 'white' }}
            >
              Delete Account
            </Button>
          </VStack>
        </VStack>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Account
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={onClose} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
};

export default Settings;